import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Minus, Plus, ShoppingBag, Trash2, ExternalLink, Loader2 } from 'lucide-react';
import { useCart } from '../context/CartContext';

const Cart = () => {
  const {
    cartItems,
    isCartOpen,
    setIsCartOpen,
    isLoading,
    removeFromCart,
    updateQuantity,
    clearCart,
    totalItems,
    totalPrice,
    checkout
  } = useCart();

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  return (
    <AnimatePresence>
      {isCartOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={() => setIsCartOpen(false)}
            data-testid="cart-overlay"
          />

          {/* Cart Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-slate-900 shadow-2xl z-50 flex flex-col"
            data-testid="cart-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-500/20 rounded-lg">
                  <ShoppingBag className="w-5 h-5 text-yellow-500" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Carrinho</h2>
                  <p className="text-sm text-slate-400">{totalItems} {totalItems === 1 ? 'item' : 'itens'}</p>
                </div>
              </div>
              <button
                onClick={() => setIsCartOpen(false)}
                className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
                data-testid="close-cart-btn"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {cartItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center" data-testid="empty-cart">
                  <ShoppingBag className="w-16 h-16 text-slate-600 mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">Carrinho vazio</h3>
                  <p className="text-slate-400 text-sm">Adicione produtos para continuar</p>
                </div>
              ) : (
                cartItems.map((item) => (
                  <motion.div
                    key={item.id}
                    layout
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    className="bg-slate-800/50 rounded-xl p-3 border border-slate-700"
                    data-testid={`cart-item-${item.id}`}
                  >
                    <div className="flex gap-3">
                      {/* Product Image */}
                      <div className="w-20 h-20 bg-slate-700 rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = '/products/placeholder.webp';
                          }}
                        />
                      </div>

                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-white font-medium text-sm truncate">{item.name}</h4>
                        {item.weight && (
                          <p className="text-slate-400 text-xs">{item.weight}</p>
                        )}
                        <p className="text-yellow-500 font-bold mt-1">{formatPrice(item.price)}</p>

                        {/* Quantity Controls */}
                        <div className="flex items-center gap-2 mt-2">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="p-1 bg-slate-700 hover:bg-slate-600 rounded transition-colors"
                            data-testid={`decrease-qty-${item.id}`}
                          >
                            <Minus className="w-4 h-4 text-slate-300" />
                          </button>
                          <span className="text-white font-medium w-8 text-center" data-testid={`qty-${item.id}`}>
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="p-1 bg-slate-700 hover:bg-slate-600 rounded transition-colors"
                            data-testid={`increase-qty-${item.id}`}
                          >
                            <Plus className="w-4 h-4 text-slate-300" />
                          </button>

                          {/* Remove Button */}
                          <button
                            onClick={() => removeFromCart(item.id)}
                            className="ml-auto p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded transition-colors"
                            data-testid={`remove-item-${item.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>

            {/* Footer */}
            {cartItems.length > 0 && (
              <div className="border-t border-slate-700 p-4 space-y-4">
                {/* Clear Cart */}
                <button
                  onClick={clearCart}
                  className="w-full text-sm text-slate-400 hover:text-red-400 transition-colors"
                  data-testid="clear-cart-btn"
                >
                  Limpar carrinho
                </button>

                {/* Total */}
                <div className="flex items-center justify-between text-lg">
                  <span className="text-slate-300">Total:</span>
                  <span className="text-white font-bold" data-testid="cart-total">{formatPrice(totalPrice)}</span>
                </div>

                {/* Checkout Button */}
                <button
                  onClick={checkout}
                  disabled={isLoading}
                  className="w-full py-4 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500 text-slate-900 font-bold rounded-xl transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-yellow-500/25"
                  data-testid="checkout-btn"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Processando...
                    </>
                  ) : (
                    <>
                      Finalizar Compra
                      <ExternalLink className="w-4 h-4" />
                    </>
                  )}
                </button>

                <p className="text-xs text-center text-slate-500">
                  Você será redirecionado para a Nuvemshop para completar o pagamento
                </p>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default Cart;
